// Gestionnaire d'authentification pour TranscriptionAI
// Gère le rafraîchissement automatique du token Firebase à partir du token Azure

import { auth, functions } from '../services/firebase/config';
import { onAuthStateChanged, onIdTokenChanged, signInWithCustomToken, signOut as firebaseSignOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

class AuthManager {
  constructor() {
    this.azureToken = null;
    this.tokenRefreshInProgress = false;
    this.tokenExpiryTime = null;
    this.isMobile = this.detectMobile();
    this.setupAuthStateListener();
    this.setupMobileOptimizations();
  }

  // Détecter si on est sur mobile
  detectMobile() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

    return isMobile;
  }

  // Optimisations spécifiques pour mobile
  setupMobileOptimizations() {
    if (this.isMobile) {

      // Surveiller la visibilité de la page pour gérer les suspensions d'app
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          // App revenue en premier plan, vérifier si on doit rafraîchir
          this.checkTokenAfterResume();
        }
      });

      // Gestion spéciale des événements de focus/blur pour mobile
      window.addEventListener('focus', () => {
        if (this.isMobile) {
          this.checkTokenAfterResume();
        }
      });

      // Intercepter les erreurs de popup spécifiques à mobile
      window.addEventListener('error', (event) => {
        if (event.message && event.message.includes('popup')) {

          this.handleMobilePopupError();
        }
      });
    }
  }

  // Vérifier le token après une reprise d'activité (mobile)
  async checkTokenAfterResume() {
    if (this.tokenRefreshInProgress) {
      return;
    }

    const lastActivity = parseInt(localStorage.getItem('app_background_time') || '0');
    const inactivityDuration = Date.now() - lastActivity;
    
    // Si l'app était inactive plus de 30 minutes ou si le token expire bientôt
    if (inactivityDuration > 30 * 60 * 1000 || this.isTokenExpiringSoon()) {

      console.log('📊 Durée d\'inactivité:', Math.round(inactivityDuration / 1000 / 60), 'minutes');
      
      // Attendre un peu que l'app soit stable
      setTimeout(() => {
        this.refreshFirebaseToken();
      }, 1000);
    }
  }

  // Vérifier si le token expire bientôt
  isTokenExpiringSoon() {
    if (!this.tokenExpiryTime) {
      return false;
    }
    
    const timeToExpiry = this.tokenExpiryTime - Date.now();
    return timeToExpiry < 10 * 60 * 1000; // Moins de 10 minutes
  }

  // Gérer les erreurs de popup sur mobile
  async handleMobilePopupError() {

    // Marquer qu'une erreur de popup s'est produite
    localStorage.setItem('mobile_popup_error', Date.now().toString());
    
    // Suggérer une reconnexion manuelle
    window.dispatchEvent(new CustomEvent('mobile-auth-error', {
      detail: {
        type: 'popup_blocked',
        message: 'Token Azure non disponible. Veuillez vous reconnecter.',
        action: 'reconnect'
      }
    }));
  }

  // Initialiser avec un token Azure existant
  async initialize(azureToken) {
    this.azureToken = azureToken;
    const userData = await this.exchangeAzureToken(azureToken);

    // TEMPORAIREMENT DÉSACTIVÉ - Causait des erreurs block_nested_popups et boucles de redirection
    // TODO: Réactiver après avoir corrigé la détection de contexte popup
    // this.scheduleAzureTokenRefresh();

    return userData;
  }

  // Convertit un token Azure en token Firebase
  async exchangeAzureToken(azureToken) {
    if (!azureToken) {

      return null;
    }

    try {
      this.tokenRefreshInProgress = true;
      
      // Appel de la fonction Firebase pour l'échange de token
      const exchangeFunction = httpsCallable(functions, 'exchangeAzureToken');
      const result = await exchangeFunction({ azureToken });
      
      // Récupérer le token Firebase et les données utilisateur
      const { firebaseToken, userData } = result.data;
      
      // Se connecter avec le token Firebase
      await signInWithCustomToken(auth, firebaseToken);
      
      // Calculer la date d'expiration (par défaut 1h pour les tokens custom)
      this.tokenExpiryTime = Date.now() + 55 * 60 * 1000; // 55 minutes

      // Planifier un rafraîchissement 5 minutes avant l'expiration
      this.scheduleTokenRefresh();
      
      return userData;
    } catch (error) {

      throw error;
    } finally {
      this.tokenRefreshInProgress = false;
    }
  }

  // Planifie un rafraîchissement automatique avant l'expiration
  scheduleTokenRefresh() {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    const timeToExpiry = this.tokenExpiryTime - Date.now();
    const refreshTime = Math.max(0, timeToExpiry - 5 * 60 * 1000); // 5 minutes avant expiration

    this.tokenRefreshTimer = setTimeout(() => {
      this.refreshFirebaseToken();
    }, refreshTime);
  }


  // Rafraîchit le token Firebase en utilisant le token Azure existant
  async refreshFirebaseToken() {
    if (this.tokenRefreshInProgress || !this.azureToken) {
      return;
    }

    try {
      this.tokenRefreshInProgress = true;
      await this.exchangeAzureToken(this.azureToken);
      
      // Déclencher un événement pour informer l'interface
      window.dispatchEvent(new CustomEvent('firebase-token-refreshed'));
      
      return true;
    } catch (error) {

      // Si le token Azure est expiré, déclencher une déconnexion
      if (error.code === 'auth/invalid-credential' || 
          error.message?.includes('Token Azure invalide')) {

        // Déclencher un événement pour informer l'interface
        window.dispatchEvent(new CustomEvent('azure-token-expired'));
        
        // Effacer le token Azure expiré
        this.azureToken = null;
      }
      
      return false;
    } finally {
      this.tokenRefreshInProgress = false;
    }
  }

  // Configuration de l'intercepteur d'erreurs d'authentification
  setupAuthErrorInterceptor() {
    // Intercepter toutes les requêtes Firebase pour détecter les erreurs d'authentification
    onIdTokenChanged(auth, async (user) => {
      if (user) {
        // Mettre à jour le token Firebase à chaque changement
        this.tokenExpiryTime = Date.now() + 55 * 60 * 1000; // 55 minutes
        this.scheduleTokenRefresh();
      }
    });
  }

  // Configurer un écouteur pour les changements d'état d'authentification
  setupAuthStateListener() {
    onAuthStateChanged(auth, (user) => {
      if (!user && this.azureToken) {
        // Si déconnecté mais qu'on a toujours un token Azure, tenter de reconnecter
        this.refreshFirebaseToken();
      }
    });
  }

  // Gestion manuelle des erreurs d'authentification dans les fonctions Firestore
  async handleFirestoreError(error, operation) {
    if (error.code === 'permission-denied' || 
        error.code === 'unauthenticated' || 
        error.message?.includes('unauthenticated')) {

      // Tenter de rafraîchir le token
      const refreshSuccess = await this.refreshFirebaseToken();
      
      // Indiquer à l'appelant s'il peut réessayer l'opération
      return refreshSuccess;
    }
    
    // Pour toute autre erreur, ne pas réessayer
    return false;
  }

  // Déconnexion complète
  async signOut() {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    this.azureToken = null;
    this.tokenExpiryTime = null;

    try {
      await firebaseSignOut(auth);
    } catch (error) {

    }
  }
}

// Exporter une instance singleton
const authManager = new AuthManager();
export default authManager; 